import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Box, 
  Grid, 
  Typography, 
  Button, 
  Container, 
  Card, 
  CardContent, 
  Fade, 
  useMediaQuery, 
  IconButton, 
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Send as SendIcon,
  TrendingUp,
  Campaign,
  AccountBalance,
  Visibility,
  Analytics,
  People,
  Speed,
  Refresh,
  Launch,
  ReceiptLong as InvoiceIcon,
  ArrowForward,
  Handshake as HandshakeIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import API from '../../API';
import CreatorDialog from '../Dashboard/Search/creatorintake';
import useAuth from '../../Hooks/use-auth';
import AgencyZapfeed from './Components/agencyZapfeed';
import { useDealFlowConnection } from './Components/useFlowConnection';

const CleanHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  padding: theme.spacing(3, 0),
  marginBottom: theme.spacing(4),
}));

const FeedCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  height: 'calc(100vh - 200px)',
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const DealFlowCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  height: 'calc(100vh - 200px)',
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  background: alpha(theme.palette.common.white, 0.1),
  backdropFilter: 'blur(10px)',
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    background: alpha(theme.palette.common.white, 0.15),
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`,
  },
}));

const CreatorCard = styled(Card)(({ theme, isActive }) => ({
  borderRadius: '12px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: isActive 
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: isActive 
    ? alpha(theme.palette.primary.main, 0.05)
    : theme.palette.background.paper,
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const DealFlowHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.12)} 100%)`,
  borderRadius: '16px 16px 0 0',
  padding: theme.spacing(3),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const CollaborationItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.main,
    transform: 'translateX(4px)',
  },
}));

export default function AgencyStart() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const agencyName = userInfo?.company_name;
  const userId = userInfo?.id;

  const [openCreatorDialog, setOpenCreatorDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Activity, 1: Collaborations, 2: Payouts

  const { data: dashboard } = useQuery(
    ['agency', 'dashboard'],
    API.agency.dashboard,
    { refetchOnWindowFocus: false }
  );

  const {
    selectedCreator,
    creators,
    creatorDetails,
    creatorsLoading,
    creatorFullDataLoading,
    handleCreatorSelect,
    clearSelections,
    getCreatorActivities,
    getCreatorCollaborations,
    getCreatorPayouts,
  } = useDealFlowConnection();

  const quickActions = [
    { 
      title: 'Add Creator', 
      icon: <PersonAddIcon />, 
      action: () => setOpenCreatorDialog(true),
      color: 'secondary',
    },
  ];

  const stats = [
    { 
      label: 'Active Creators', 
      value: dashboard?.creators || 0, 
      icon: <People />, 
      color: 'primary',
      action: () => navigate('/agency/creators'),
    },
    { 
      label: 'Active Deals', 
      value: (dashboard?.active_campaigns || 0) + (dashboard?.active_partnerships || 0), 
      icon: <Campaign />, 
      color: 'info',
      action: () => navigate('/agency/all-collaborations'),
    },
  ];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const collaborations = getCreatorCollaborations();
  const payouts = getCreatorPayouts();

  const getStatusColor = (status, type) => {
    if (type === 'campaign') {
      switch (status?.toLowerCase()) {
        case 'pitched': return 'warning';
        case 'accepted': return 'info';
        case 'completed': return 'success';
        default: return 'default';
      }
    } else if (type === 'partnership') {
      switch (status?.toLowerCase()) {
        case 'pending': return 'warning';
        case 'active': return 'info';
        case 'completed': return 'success';
        default: return 'default';
      }
    } else if (type === 'payout') {
      switch (status) {
        case 'pending': return 'warning';
        case 'approved': return 'info';
        case 'paid': return 'success';
        default: return 'default';
      }
    }
    return 'default';
  };

  // Mobile view
  if (isMobile) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Mobile Header */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          p: 2,
          color: 'white'
        }}
        data-tour="agency-overview"
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {agencyName}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Creator Management Dashboard
          </Typography>
        </Box>

        {/* Mobile Quick Actions */}
        <Box sx={{ 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider',
          p: 1
        }}>
          <Box sx={{ 
            display: 'flex', 
            overflowX: 'auto',
            gap: 1,
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none'
          }}>
            {quickActions.map((action) => (
              <Button
                key={action.title}
                variant="outlined"
                color={action.color}
                size="small"
                onClick={action.action}
                startIcon={action.icon}
                sx={{
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 1
                }}
              >
                {action.title}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Mobile Stats */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2}>
            {stats.map((stat) => (
              <Grid item xs={6} key={stat.label}>
                <Card 
                  onClick={stat.action}
                  sx={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ color: `${stat.color}.main`, mb: 1 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} color={`${stat.color}.main`}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Mobile Activity Feed */}
        <Box sx={{ p: 2, pt: 0 }} data-tour="agency-activity-feed">
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Live Activity Feed
          </Typography>
          <AgencyZapfeed />
        </Box>
      </Box>
    );
  }

  // Desktop view
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <CleanHeader>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box data-tour="agency-overview">
              <Typography variant="h4" sx={{ 
                fontWeight: 800, 
                color: 'white',
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}>
                {agencyName}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: alpha(theme.palette.common.white, 0.8),
                fontWeight: 500,
              }}>
                Creator Management Dashboard
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Stack direction="row" spacing={1}>
                {quickActions.map((action) => (
                  <Button
                    key={action.title}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={action.action}
                    startIcon={action.icon}
                    sx={{
                      color: 'white',
                      borderColor: alpha(theme.palette.common.white, 0.3),
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                      },
                    }}
                  >
                    {action.title}
                  </Button>
                ))}
              </Stack>
              
              <Chip 
                label="Live"
                size="small"
                sx={{ 
                  bgcolor: alpha(theme.palette.success.main, 0.2),
                  color: 'white',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                }}
              />
              <IconButton 
                sx={{ 
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) }
                }}
                onClick={() => window.location.reload()}
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {stats.map((stat) => (
              <Grid item xs={3} key={stat.label}>
                <StatCard onClick={stat.action}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8), mb: 0.5 }}>
                          {stat.label}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                          {stat.value}
                        </Typography>
                      </Box>
                      <Box sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
                        {stat.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </StatCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </CleanHeader>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Grid container spacing={3}>
          {/* Activity Feed - Left Side */}
          <Grid item xs={12} md={7}>
            <Fade in={true} timeout={400}>
              <FeedCard data-tour="agency-activity-feed">
                <Box sx={{ 
                  p: 3, 
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      Live Activity Feed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Real-time creator deals, payments, and collaborations
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  height: 'calc(100vh - 340px)',
                  overflow: 'auto',
                }}>
                  <AgencyZapfeed onCreatorSelect={handleCreatorSelect} />
                </Box>
              </FeedCard>
            </Fade>
          </Grid>

          {/* Live Deal Flow - Right Side */}
          <Grid item xs={12} md={5}>
            <Fade in={true} timeout={600}>
              <DealFlowCard data-tour="agency-deal-flow">
                <DealFlowHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                        Active Creators
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha(theme.palette.text.primary, 0.7) }}>
                        {selectedCreator ? 'Selected creator details' : `${creators?.length || 0} creators in roster`}
                      </Typography>
                    </Box>
                    {selectedCreator ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          clearSelections();
                          setSelectedTab(0);
                        }}
                      >
                        Clear
                      </Button>
                    ) : (
                      <Chip 
                        label={`${creators?.length || 0}`}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Box>
                  
                  {!selectedCreator && (
                    <Stack direction="row" spacing={1} data-tour="roster-list">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<People />}
                        onClick={() => navigate('/agency/creators')}
                        sx={{ flex: 1 }}
                      >
                        All Creators
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenCreatorDialog(true)}
                        sx={{ flex: 1 }}
                      >
                        Add New
                      </Button>
                    </Stack>
                  )}
                </DealFlowHeader>

                <Box sx={{ 
                  height: 'calc(100vh - 400px)',
                  overflow: 'auto',
                }}>
                  {selectedCreator && creatorDetails ? (
                    // Selected Creator Detail View with Tabs
                    <Box>
                      <Box sx={{ textAlign: 'center', mb: 3, p: 3 }}>
                        <Avatar
                          src={creatorDetails.pfphref}
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 2,
                            border: `3px solid ${theme.palette.primary.main}`,
                          }}
                        >
                          {selectedCreator.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="h6" fontWeight={700}>
                          @{creatorDetails.display_name || selectedCreator}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {creatorDetails.notes_content_style || 'Creator'}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ px: 3, mb: 2 }}>
                        <Stack spacing={1.5}>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Analytics />}
                            onClick={() => navigate(`/agency/creators/${selectedCreator}`)}
                          >
                            View Full Profile
                          </Button>
                          
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<SettingsIcon />}
                            onClick={() => navigate(`/creator/${selectedCreator}/edit`)}
                          >
                            Edit Creator
                          </Button>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Tabs */}
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs 
                          value={selectedTab} 
                          onChange={handleTabChange}
                          variant="fullWidth"
                          sx={{ px: 2 }}
                        >
                          <Tab 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUp fontSize="small" />
                                Activity
                              </Box>
                            } 
                          />
                          <Tab 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <HandshakeIcon fontSize="small" />
                                Deals
                                <Chip 
                                  label={collaborations.all.length} 
                                  size="small" 
                                  sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            } 
                          />
                          <Tab 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoneyIcon fontSize="small" />
                                Payouts
                                <Chip 
                                  label={payouts.length} 
                                  size="small" 
                                  sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            } 
                          />
                        </Tabs>
                      </Box>

                      {/* Tab Content */}
                      <Box sx={{ p: 3 }}>
                        {creatorFullDataLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            {/* Activity Tab */}
                            {selectedTab === 0 && (
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Recent Activities
                                </Typography>
                                {getCreatorActivities(selectedCreator).length > 0 ? (
                                  getCreatorActivities(selectedCreator).slice(0, 5).map((activity) => (
                                    <Box 
                                      key={activity.id}
                                      sx={{ 
                                        p: 2, 
                                        mb: 1, 
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                          <Typography variant="body2" fontWeight={600}>
                                            {activity.type}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {activity.subtitle}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2" color="primary" fontWeight={700}>
                                          ${activity.price.toLocaleString()}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                    No recent activities
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Collaborations Tab */}
                            {selectedTab === 1 && (
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                  Active Deals ({collaborations.all.length})
                                </Typography>
                                {collaborations.all.length > 0 ? (
                                  collaborations.all.map((collab) => (
                                    <CollaborationItem
                                      key={`${collab.type}-${collab.id}`}
                                      onClick={() => navigate(collab.link)}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Box sx={{ 
                                          p: 1, 
                                          borderRadius: 1.5, 
                                          bgcolor: alpha(
                                            collab.type === 'campaign' 
                                              ? theme.palette.primary.main 
                                              : theme.palette.success.main, 
                                            0.1
                                          )
                                        }}>
                                          {collab.type === 'campaign' ? (
                                            <Campaign sx={{ color: 'primary.main', fontSize: 20 }} />
                                          ) : (
                                            <HandshakeIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                          )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={600} noWrap>
                                            {collab.name}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Chip
                                              label={collab.status}
                                              size="small"
                                              color={getStatusColor(collab.status, collab.type)}
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                              ${collab.price.toLocaleString()}
                                            </Typography>
                                          </Box>
                                          {collab.dueDate && (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                              Due: {new Date(collab.dueDate).toLocaleDateString()}
                                            </Typography>
                                          )}
                                        </Box>
                                        <ArrowForward fontSize="small" sx={{ color: 'action.active', mt: 0.5 }} />
                                      </Box>
                                    </CollaborationItem>
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No active collaborations
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Payouts Tab */}
                            {selectedTab === 2 && (
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                  Payment History ({payouts.length})
                                </Typography>
                                {payouts.length > 0 ? (
                                  <>
                                    {/* Summary */}
                                    <Box sx={{ 
                                      p: 2, 
                                      mb: 2, 
                                      borderRadius: 2,
                                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            Total Paid
                                          </Typography>
                                          <Typography variant="h6" fontWeight={700} color="success.main">
                                            ${payouts.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography variant="caption" color="text.secondary">
                                            Pending
                                          </Typography>
                                          <Typography variant="h6" fontWeight={700} color="warning.main">
                                            ${payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Box>

                                    {/* Payout List */}
                                    {payouts.slice(0, 10).map((payout) => (
                                      <CollaborationItem
                                        key={payout.id}
                                        onClick={() => navigate(`/agency/creators/${selectedCreator}/payouts`)}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                          <Box sx={{ 
                                            p: 1, 
                                            borderRadius: 1.5, 
                                            bgcolor: alpha(theme.palette.success.main, 0.1)
                                          }}>
                                            {payout.status === 'paid' ? (
                                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                            ) : (
                                              <PendingIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                                            )}
                                          </Box>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                              {payout.poNumber || `Payout #${payout.id}`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                              <Chip
                                                label={payout.status}
                                                size="small"
                                                color={getStatusColor(payout.status, 'payout')}
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                              />
                                              <Typography variant="body2" color="success.main" fontWeight={700}>
                                                ${payout.amount.toLocaleString()}
                                              </Typography>
                                            </Box>
                                            {payout.campaignName && (
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                {payout.campaignName}
                                              </Typography>
                                            )}
                                            {payout.payoutDate && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                {new Date(payout.payoutDate).toLocaleDateString()}
                                              </Typography>
                                            )}
                                          </Box>
                                          <ArrowForward fontSize="small" sx={{ color: 'action.active', mt: 0.5 }} />
                                        </Box>
                                      </CollaborationItem>
                                    ))}
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No payouts recorded
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    // Creator Roster List
                    <Box sx={{ p: 3 }}>
                      {creatorsLoading ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          Loading creators...
                        </Typography>
                      ) : creators && creators.length > 0 ? (
                        <>
                          {creators.slice(0, 10).map((creator) => (
                            <CreatorCard 
                              key={creator.creator}
                              isActive={selectedCreator === creator.creator}
                              onClick={() => handleCreatorSelect(creator.creator)}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar
                                    src={creator.pfphref}
                                    sx={{ width: 48, height: 48 }}
                                  >
                                    {creator.creator.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                                      @{creator.display_name || creator.creator}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {creator.notes_content_style || 'Creator'}
                                    </Typography>
                                  </Box>
                                  <ArrowForward color="action" />
                                </Box>
                              </CardContent>
                            </CreatorCard>
                          ))}
                          
                          {creators.length > 10 && (
                            <Button
                              fullWidth
                              variant="outlined"
                              endIcon={<Launch />}
                              onClick={() => navigate('/agency/creators')}
                              sx={{ mt: 2 }}
                            >
                              View All {creators.length} Creators
                            </Button>
                          )}
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No creators found
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </DealFlowCard>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Creator Dialog */}
      <CreatorDialog
        open={openCreatorDialog}
        onClose={() => setOpenCreatorDialog(false)}
        defaultManager={agencyName}
      />
    </Box>
  );
}